import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme,
  Collapse,
} from "@mui/material";

import StepperIcon from "./StepperIcon";
import FleetInquiryLabel from "./FleetInquiryLabel";
import FleetInquiryContent from "./FleetInquiryContent";
import {
  stepperContentStyles,
  getStepLabelStyles,
  getStepperStyles,
  getHorizontalStepperStyles,
  getHorizontalStepperLabelStyles,
} from "./styles";
import useResponsive from "@/hooks/useResponsive";

interface IProps {
  steps: {
    title: string;
    details: string[];
    description: string;
  }[];
}

const FleetInquiryStepper: React.FC<IProps> = ({ steps }) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const stepperRef = useRef<HTMLElement>();
  const containerRef = useRef<HTMLElement>();

  const theme = useTheme();
  const isMobile = useResponsive("down", "lg");
  const ICON_SIZE = 44;
  const stepperStyles = isMobile
    ? getHorizontalStepperStyles(theme, ICON_SIZE)
    : getStepperStyles(theme, ICON_SIZE);

  // Handling scroll events
  useEffect(() => {
    const handleScroll = () => {
      if (stepperRef.current && containerRef.current) {
        const value =
          stepperRef.current.offsetTop - containerRef.current.offsetTop;

        if (value >= 0 && value < 1500) {
          const step = Math.floor(value / 300);
          setActiveStep(step);
        }
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup function to remove scroll event listener
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeStep]);

  return (
    <Box sx={{ height: "2000px" }} mx="auto" ref={containerRef}>
      <Box ref={stepperRef} sx={{ position: "sticky", top: "48px" }}>
        <Stepper
          activeStep={activeStep}
          orientation={isMobile ? "horizontal" : "vertical"}
          alternativeLabel={isMobile ? true : false}
          sx={stepperStyles}
        >
          {steps.map(({ title, description, details }, index) => (
            <Step key={index} id={`step-${index}`}>
              <StepLabel
                icon={<StepperIcon index={index} activeStep={activeStep} />}
                sx={
                  isMobile
                    ? getHorizontalStepperLabelStyles(index, ICON_SIZE)
                    : getStepLabelStyles(ICON_SIZE)
                }
              >
                {activeStep === index && (
                  <FleetInquiryLabel
                    title={title}
                    type={isMobile ? "horizontal" : "vertical"}
                    index={index}
                  />
                )}
              </StepLabel>
              {!isMobile && (
                <StepContent sx={stepperContentStyles}>
                  <FleetInquiryContent
                    details={details}
                    description={description}
                  />
                </StepContent>
              )}
            </Step>
          ))}
        </Stepper>

        {isMobile && (
          <>
            {steps.map(({ details, description }, index) => (
              <Collapse in={activeStep === index} key={index}>
                <Box px={2}>
                  <FleetInquiryContent
                    details={details}
                    description={description}
                  />
                </Box>
              </Collapse>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default FleetInquiryStepper;
